/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import { useLayoutEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { sendSentryError } from '../../utils/sentry';
import { useAdminRemoveUserKeysMutation } from '../../apollo';
import { useAlert, useModals } from '../../providers';
import { Button as DefButton } from '../buttons';

const RemoveUserKeysAdminModal = () => {
  const { t } = useTranslation();
  const { setAlert } = useAlert();
  const { data: { userId, exchangeId } = {}, dispatch } =
    useModals<'removeUserKeysAdmin'>();

  useLayoutEffect(() => {
    dispatch({
      type: 'setWrapperProps',
      payload: {
        modalName: 'rebalance-user-admin',
        titleText: t('REMOVE_USER_KEYS_ADMIN_MODAL__title'),
      },
    });
  }, [dispatch, t]);

  const [removeUserKeys, { loading: removingUserKeys }] =
    useAdminRemoveUserKeysMutation({
      onCompleted: () => {
        setAlert({
          type: 'success',
          message: t('REMOVE_USER_KEYS_ADMIN_MODAL__success'),
        });
        dispatch({ type: 'closeModal' });
      },
      onError: (error) => {
        setAlert({
          type: 'error',
          message: t('REMOVE_USER_KEYS_ADMIN_MODAL__error'),
        });
        sendSentryError(error);
      },
    });

  const active = !!userId && !!exchangeId;

  return (
    <Button
      loading={removingUserKeys}
      disabled={!active}
      onClick={() => {
        if (active) {
          removeUserKeys({
            variables: {
              userId,
              exchangeId,
            },
          });
        }
      }}
    >
      {t('REMOVE_USER_KEYS_ADMIN_MODAL__button')}
    </Button>
  );
};

export type RemoveUserKeysAdminModalData = {
  userId: number;
  exchangeId: number;
};

const Button = styled(DefButton)`
  width: 100%;
  margin-top: 6px;
`;

export { RemoveUserKeysAdminModal };
